export const SITE_URL = "https://steriahouse.id";
export const DEFAULT_OG_IMAGE = "/og-steria-house.png";
export const DEFAULT_SEARCH_IMAGE = "/steria-house-search-preview.jpg";

export const DEFAULT_SEO = {
  title: "Steria House | Intelligent Digital Experiences",
  description:
    "Steria House is a digital product studio creating purposeful software, AI experiences, and custom technology for ideas that improve how people live.",
};

type StructuredData = Record<string, unknown> | Record<string, unknown>[];

interface PageMetadata {
  title: string;
  description: string;
  canonicalPath?: string;
  siteUrl?: string;
  image?: string;
  imageAlt?: string;
  type?: "website" | "article";
  noindex?: boolean;
  structuredData?: StructuredData;
}

function absoluteUrl(value: string, siteUrl = SITE_URL) {
  return new URL(value, `${siteUrl}/`).toString();
}

function setMeta(attribute: "name" | "property", key: string, content: string) {
  let element = document.head.querySelector<HTMLMetaElement>(`meta[${attribute}="${key}"]`);
  if (!element) {
    element = document.createElement("meta");
    element.setAttribute(attribute, key);
    document.head.appendChild(element);
  }
  element.content = content;
}

function setCanonical(url: string) {
  let element = document.head.querySelector<HTMLLinkElement>('link[rel="canonical"]');
  if (!element) {
    element = document.createElement("link");
    element.rel = "canonical";
    document.head.appendChild(element);
  }
  element.href = url;
}

function setLanguageAlternates(url: string) {
  document.head
    .querySelectorAll<HTMLLinkElement>('link[rel="alternate"][hreflang]')
    .forEach((element) => {
      element.href = url;
    });
}

function setStructuredData(value?: StructuredData) {
  const id = "page-structured-data";
  const current = document.getElementById(id);
  if (!value) {
    current?.remove();
    return;
  }

  const element =
    current instanceof HTMLScriptElement ? current : document.createElement("script");
  element.id = id;
  element.type = "application/ld+json";
  element.textContent = JSON.stringify(value);
  if (!element.isConnected) document.head.appendChild(element);
}

export function applyPageMetadata({
  title,
  description,
  canonicalPath = "/",
  siteUrl = SITE_URL,
  image = DEFAULT_OG_IMAGE,
  imageAlt = "Steria House intelligent digital experiences",
  type = "website",
  noindex = false,
  structuredData,
}: PageMetadata) {
  const canonicalUrl = absoluteUrl(canonicalPath, siteUrl);
  const imageUrl = absoluteUrl(image, siteUrl);

  document.title = title;
  document.documentElement.lang = "en";
  setCanonical(canonicalUrl);
  setLanguageAlternates(canonicalUrl);

  setMeta("name", "description", description);
  setMeta(
    "name",
    "robots",
    noindex
      ? "noindex, nofollow, noarchive"
      : "index, follow, max-image-preview:large, max-snippet:-1, max-video-preview:-1",
  );

  setMeta("property", "og:type", type);
  setMeta("property", "og:site_name", "Steria House");
  setMeta("property", "og:locale", "en_US");
  setMeta("property", "og:url", canonicalUrl);
  setMeta("property", "og:title", title);
  setMeta("property", "og:description", description);
  setMeta("property", "og:image", imageUrl);
  setMeta("property", "og:image:secure_url", imageUrl);
  setMeta("property", "og:image:type", "image/png");
  setMeta("property", "og:image:width", "1200");
  setMeta("property", "og:image:height", "630");
  setMeta("property", "og:image:alt", imageAlt);

  setMeta("name", "twitter:card", "summary_large_image");
  setMeta("name", "twitter:url", canonicalUrl);
  setMeta("name", "twitter:title", title);
  setMeta("name", "twitter:description", description);
  setMeta("name", "twitter:image", imageUrl);
  setMeta("name", "twitter:image:alt", imageAlt);

  setStructuredData(structuredData);
}

export function buildLandingStructuredData(products: Array<{ name: string; slug: string }>) {
  return {
    "@context": "https://schema.org",
    "@graph": [
      {
        "@type": "Organization",
        "@id": `${SITE_URL}/#organization`,
        name: "Steria House",
        url: `${SITE_URL}/`,
        logo: {
          "@type": "ImageObject",
          url: `${SITE_URL}/icon-512.png`,
          width: 512,
          height: 512,
        },
        image: `${SITE_URL}${DEFAULT_SEARCH_IMAGE}`,
        email: "mailto:steriahouse@gmail.com",
        description:
          "A digital product studio creating purposeful software, AI experiences, and custom technology.",
      },
      {
        "@type": "WebSite",
        "@id": `${SITE_URL}/#website`,
        url: `${SITE_URL}/`,
        name: "Steria House",
        publisher: { "@id": `${SITE_URL}/#organization` },
        inLanguage: "en",
      },
      {
        "@type": "CollectionPage",
        "@id": `${SITE_URL}/#webpage`,
        url: `${SITE_URL}/`,
        name: DEFAULT_SEO.title,
        isPartOf: { "@id": `${SITE_URL}/#website` },
        about: { "@id": `${SITE_URL}/#organization` },
        description: DEFAULT_SEO.description,
        primaryImageOfPage: {
          "@type": "ImageObject",
          url: `${SITE_URL}${DEFAULT_SEARCH_IMAGE}`,
          width: 1200,
          height: 630,
          caption: "Wisteria growing through a purposeful digital environment",
        },
        mainEntity: {
          "@type": "ItemList",
          name: "Steria House products",
          numberOfItems: products.length,
          itemListElement: products.map((product, index) => ({
            "@type": "ListItem",
            position: index + 1,
            name: product.name,
            url: `${SITE_URL}/projects/${product.slug}`,
          })),
        },
        inLanguage: "en",
      },
    ],
  };
}

export function buildProductStructuredData({
  name,
  slug,
  description,
  category,
  platform,
  image,
  websiteUrl,
}: {
  name: string;
  slug: string;
  description: string;
  category: string;
  platform: string;
  image: string;
  websiteUrl?: string;
}) {
  const url = `${SITE_URL}/projects/${slug}`;
  const imageUrl = absoluteUrl(image);
  return {
    "@context": "https://schema.org",
    "@graph": [
      {
        "@type": "Organization",
        "@id": `${SITE_URL}/#organization`,
        name: "Steria House",
        url: `${SITE_URL}/`,
        logo: {
          "@type": "ImageObject",
          url: `${SITE_URL}/icon-512.png`,
          width: 512,
          height: 512,
        },
      },
      {
        "@type": "WebSite",
        "@id": `${SITE_URL}/#website`,
        url: `${SITE_URL}/`,
        name: "Steria House",
        publisher: { "@id": `${SITE_URL}/#organization` },
        inLanguage: "en",
      },
      {
        "@type": "WebPage",
        "@id": `${url}#webpage`,
        url,
        name: `${name} | Steria House`,
        description,
        isPartOf: { "@id": `${SITE_URL}/#website` },
        about: { "@id": `${url}#software` },
        primaryImageOfPage: {
          "@type": "ImageObject",
          url: imageUrl,
        },
        inLanguage: "en",
      },
      {
        "@type": "SoftwareApplication",
        "@id": `${url}#software`,
        name,
        description,
        applicationCategory: category,
        operatingSystem: platform,
        url: websiteUrl || url,
        image: imageUrl,
        creator: { "@id": `${SITE_URL}/#organization` },
      },
      {
        "@type": "BreadcrumbList",
        itemListElement: [
          {
            "@type": "ListItem",
            position: 1,
            name: "Steria House",
            item: `${SITE_URL}/`,
          },
          {
            "@type": "ListItem",
            position: 2,
            name,
            item: url,
          },
        ],
      },
    ],
  };
}
